import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Columns3,
  Rows3,
  Plus,
  Minus,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  X,
  Merge,
  Split,
  Palette,
  Grid3X3
} from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from './ui/select';
import { Label } from './ui/label';

const TableControlPanel = ({ editor, onClose, position, portalContainer, inSidebar = false }) => {
  const [rowsToAdd, setRowsToAdd] = useState(1);
  const [colsToAdd, setColsToAdd] = useState(1);
  const [cellBgColor, setCellBgColor] = useState('#ffffff');

  if (!editor) return null;

  // Structure controls - Add/Remove rows and columns
  const addRowAbove = () => {
    for (let i = 0; i < rowsToAdd; i++) {
      editor.chain().focus().addRowBefore().run();
    }
  };

  const addRowBelow = () => {
    for (let i = 0; i < rowsToAdd; i++) {
      editor.chain().focus().addRowAfter().run();
    }
  };

  const deleteRow = () => {
    editor.chain().focus().deleteRow().run();
  };

  const addColumnLeft = () => {
    for (let i = 0; i < colsToAdd; i++) {
      editor.chain().focus().addColumnBefore().run();
    }
  };

  const addColumnRight = () => {
    for (let i = 0; i < colsToAdd; i++) {
      editor.chain().focus().addColumnAfter().run();
    }
  };

  const deleteColumn = () => {
    editor.chain().focus().deleteColumn().run();
  };

  const deleteTable = () => {
    editor.chain().focus().deleteTable().run();
    onClose();
  };

  const mergeCells = () => {
    editor.chain().focus().mergeCells().run();
  };

  const splitCell = () => {
    editor.chain().focus().splitCell().run();
  };

  // Content alignment controls
  const setAlignment = (h, v) => {
    editor.chain().focus().setTextAlign(h).run();
    editor.chain().focus().setCellAttribute('verticalAlign', v).run();
  };

  // Cell Design
  const handleCellBgColorChange = (e) => {
    const color = e.target.value;
    setCellBgColor(color);
    editor.chain().focus().setCellAttribute('backgroundColor', color).run();
  };

  // Table Positioning
  const setTablePosition = (position) => {
    const { state } = editor;
    const { selection } = state;
    let tablePos = null;

    // Find the table node
    state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      if (node.type.name === 'table' && !tablePos) {
        tablePos = pos;
        return false;
      }
    });

    if (tablePos !== null) {
      let alignClass = '';
      switch (position) {
        case 'left':
          alignClass = 'table-align-left';
          break;
        case 'center':
          alignClass = 'table-align-center';
          break;
        case 'right':
          alignClass = 'table-align-right';
          break;
        default:
          alignClass = '';
      }

      editor.chain().focus().updateAttributes('table', {
        class: alignClass
      }).run();
    }
  };

  // If in sidebar, don't render header or wrapper - just the content
  if (inSidebar) {
    return (
      <div className="space-y-6 table-control-panel">

      {/* Structure Controls */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100 border-b pb-2">Estructura</h3>
        
        {/* Filas */}
        <div>
          <h4 className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2">
            <Rows3 className="w-3 h-3" />
            Filas
          </h4>
          <div className="flex gap-2 mb-2">
            <Input
              type="number"
              min="1"
              max="10"
              value={rowsToAdd}
              onChange={(e) => setRowsToAdd(parseInt(e.target.value) || 1)}
              className="w-16 h-8 text-sm"
            />
            <div className="flex gap-1 flex-1">
              <Button onClick={addRowAbove} size="sm" variant="outline" className="flex-1 text-xs h-8" title="Insertar Arriba">
                <Plus className="w-3 h-3 mr-1" /> Arriba
              </Button>
              <Button onClick={addRowBelow} size="sm" variant="outline" className="flex-1 text-xs h-8" title="Insertar Abajo">
                <Plus className="w-3 h-3 mr-1" /> Abajo
              </Button>
            </div>
            <Button onClick={deleteRow} size="sm" variant="ghost" className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 h-8 w-8 p-0" title="Eliminar Fila">
              <Minus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Columnas */}
        <div>
          <h4 className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2 flex items-center gap-2">
            <Columns3 className="w-3 h-3" />
            Columnas
          </h4>
          <div className="flex gap-2 mb-2">
            <Input
              type="number"
              min="1"
              max="10"
              value={colsToAdd}
              onChange={(e) => setColsToAdd(parseInt(e.target.value) || 1)}
              className="w-16 h-8 text-sm"
            />
            <div className="flex gap-1 flex-1">
              <Button onClick={addColumnLeft} size="sm" variant="outline" className="flex-1 text-xs h-8" title="Insertar Izquierda">
                <Plus className="w-3 h-3 mr-1" /> Izq
              </Button>
              <Button onClick={addColumnRight} size="sm" variant="outline" className="flex-1 text-xs h-8" title="Insertar Derecha">
                <Plus className="w-3 h-3 mr-1" /> Der
              </Button>
            </div>
            <Button onClick={deleteColumn} size="sm" variant="ghost" className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40 h-8 w-8 p-0" title="Eliminar Columna">
              <Minus className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Merge/Split */}
        <div className="flex gap-2 pt-2">
          <Button onClick={mergeCells} size="sm" variant="secondary" className="flex-1 text-xs h-8">
            <Merge className="w-3 h-3 mr-2" /> Combinar
          </Button>
          <Button onClick={splitCell} size="sm" variant="secondary" className="flex-1 text-xs h-8">
            <Split className="w-3 h-3 mr-2" /> Dividir
          </Button>
        </div>
      </div>

      {/* Design Controls */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100 border-b pb-2">Diseño</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-600 dark:text-slate-300">Sombreado de Celda</Label>
            <div className="flex items-center gap-2">
              <div className="relative w-8 h-8 overflow-hidden rounded border border-slate-200 dark:border-slate-600">
                <Input
                  type="color"
                  value={cellBgColor}
                  onChange={handleCellBgColorChange}
                  className="absolute -top-1 -left-1 w-10 h-10 p-0 border-0 cursor-pointer"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alignment Controls */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100 border-b pb-2">Alineación</h3>
        
        <div className="flex justify-center py-2">
          <div className="grid grid-cols-3 gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-md">
            {/* Top Row */}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('left', 'top')} title="Arriba Izquierda">
              <AlignLeft className="w-3 h-3 rotate-90 origin-center transform translate-y-[-2px]" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('center', 'top')} title="Arriba Centro">
              <AlignVerticalJustifyStart className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('right', 'top')} title="Arriba Derecha">
              <AlignRight className="w-3 h-3 rotate-[-90] origin-center transform translate-y-[-2px]" />
            </Button>

            {/* Middle Row */}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('left', 'middle')} title="Centro Izquierda">
              <AlignLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('center', 'middle')} title="Centro Centro">
              <AlignVerticalJustifyCenter className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('right', 'middle')} title="Centro Derecha">
              <AlignRight className="w-4 h-4" />
            </Button>

            {/* Bottom Row */}
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('left', 'bottom')} title="Abajo Izquierda">
              <AlignLeft className="w-3 h-3 rotate-[-90] origin-center transform translate-y-[2px]" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('center', 'bottom')} title="Abajo Centro">
              <AlignVerticalJustifyEnd className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-white dark:hover:bg-slate-700" onClick={() => setAlignment('right', 'bottom')} title="Abajo Derecha">
              <AlignRight className="w-3 h-3 rotate-90 origin-center transform translate-y-[2px]" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table Positioning */}
      <div className="space-y-4">
        <h3 className="font-semibold text-sm text-slate-800 dark:text-slate-100 border-b pb-2">Posición Tabla</h3>
        <Select onValueChange={setTablePosition} defaultValue="left">
          <SelectTrigger className="h-8 text-sm w-full">
            <SelectValue placeholder="Seleccionar posición" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Alinear a la Izquierda</SelectItem>
            <SelectItem value="center">Centrar en Página</SelectItem>
            <SelectItem value="right">Alinear a la Derecha</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Delete Table */}
      <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
        <Button
          onClick={deleteTable}
          size="sm"
          variant="destructive"
          className="w-full"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Eliminar Tabla Completa
        </Button>
      </div>
      </div>
    );
  }

  // Floating panel mode (not used anymore, but keeping for compatibility)
  return null;
};

// Helper icons for alignment grid (if needed, but using standard Lucide icons above)
import { Trash2 } from 'lucide-react';

export default TableControlPanel;
